import { createSlice } from '@reduxjs/toolkit';
import { PAGINATION } from '@/constants/pagination';

// todo: need to remove once api is done
const reducerName = 'atsJobDetails';

type ApiSlice<T = any> = {
  isLoading: boolean;
  data: T;
  status: string | undefined;
  error: string | undefined;
};

type FiltersState = {
  is_ai_review_failed_applied?: boolean;
  isFilterApplied?: boolean;
  [key: string]: any;
};

type StageCountEntry = {
  stage_id: string | number;
  applications_count: number;
  [key: string]: any;
};

type StagesCountData = {
  stage_counts?: StageCountEntry[];
  total_applications?: number | string;
  [key: string]: any;
};

type RejectedCountData = {
  total_applications?: number | string;
  [key: string]: any;
};

type AtsJobDetailsState = {
  currentStage: { id: string; name: string; [key: string]: any };
  currentMode: string;
  searchTerm: string | null;
  filters: FiltersState;
  reloadConfig: any;
  isFilterApplied: boolean;
  page: {
    number: number;
    offset: number;
    limit: number;
    scroll_token: string | null;
  };
  sort: { id: string; label: string; element: string; order: string };
  candidateList: ApiSlice;
  kanbanList: ApiSlice;
  getStagesCount: ApiSlice<StagesCountData>;
  rejectedCount: ApiSlice<RejectedCountData>;
  createContact: ApiSlice;
  currentApplication: ApiSlice;
  currentJobActivities: ApiSlice;
  currentJobActivitiesPage: { page: number; limit: number };
  currentApplicationActivities: ApiSlice;
  currentApplicationActivitiesPage: { page: number; limit: number };
  filterWithValues: any[];
  scorecardParams: {
    department_ids: any[];
    office_ids: any[];
    employment_types: any[];
    ats_work_type_ids: any[];
  };
  // Referenced by `clearData` but never explicitly initialized.
  currentView?: any;
};

const initialState: AtsJobDetailsState = {
  currentStage: {
    id: 'all_stages',
    name: 'All Stages',
  },
  currentMode: 'active',
  searchTerm: null,
  filters: {},
  reloadConfig: {},
  isFilterApplied: false,
  page: {
    number: 0,
    offset: 0,
    limit: PAGINATION.APPLICATION,
    scroll_token: null,
  },
  sort: {
    id: 'updated_at',
    label: 'Last activity (New to old)',
    element: 'updated_at',
    order: 'desc',
  },
  candidateList: {
    isLoading: true,
    data: {},
    status: undefined,
    error: undefined,
  },
  kanbanList: {
    isLoading: true,
    data: {},
    status: undefined,
    error: undefined,
  },
  getStagesCount: {
    isLoading: true,
    data: {},
    status: undefined,
    error: undefined,
  },
  rejectedCount: {
    isLoading: true,
    data: {},
    status: undefined,
    error: undefined,
  },
  createContact: {
    isLoading: true,
    data: {},
    status: undefined,
    error: undefined,
  },
  currentApplication: {
    error: undefined,
    isLoading: true,
    status: undefined,
    data: {},
  },
  currentJobActivities: {
    error: undefined,
    isLoading: true,
    status: undefined,
    data: {},
  },
  currentJobActivitiesPage: {
    page: 0,
    limit: PAGINATION.APPLICATION,
  },
  currentApplicationActivities: {
    error: undefined,
    isLoading: true,
    status: undefined,
    data: {},
  },
  currentApplicationActivitiesPage: {
    page: 0,
    limit: PAGINATION.APPLICATION,
  },
  filterWithValues: [],
  scorecardParams: {
    department_ids: [],
    office_ids: [],
    employment_types: [],
    ats_work_type_ids: [],
  },
};

const { reducer, actions: atsJobDetailsActions } = createSlice({
  name: reducerName,
  initialState,

  reducers: {
    applySort: (state, { payload }) => {
      state.sort = payload;
      // Reset page and scroll_token when sort changes, but preserve the current limit
      state.page = { ...initialState.page, limit: state.page.limit };
    },

    resetSortToInitial: (state) => {
      state.sort = initialState?.sort;
    },

    applyAIReviewFilter: (state) => {
      state.filters = {
        is_ai_review_failed_applied: true,
      };
      state.filterWithValues = [
        {
          filter_by: {
            label: 'AI screening score status',
            value: 'ai_review_status',
          },
          filter_condition: {
            value: 'is',
            label: 'Include any',
          },
          filter_by_values: ['failed'],
        },
      ];
      // Like every other filter change: drop the previous list's page/cursor.
      state.page = { ...initialState.page, limit: state.page.limit };
    },

    resetAIReviewFilter: (state) => {
      state.filters.is_ai_review_failed_applied = false;
      state.filterWithValues = state.filterWithValues.filter(
        (filter) => filter.filter_by.value !== 'ai_review_status'
      );
    },

    setCurrentStage: (state, { payload: { stage } }) => {
      state.currentStage = stage;
      // Reset page and scroll_token when stage changes, but preserve the current limit
      state.page = { ...initialState.page, limit: state.page.limit };
      // Clear candidate list data to prevent old data from showing while loading new stage
      state.candidateList = {
        ...state.candidateList,
        data: {},
        isLoading: true,
      };
    },

    setCurrentMode: (state, { payload: { mode } }) => {
      state.currentMode = mode;
      // Reset page and scroll_token when mode changes, but preserve the current limit
      state.page = { ...initialState.page, limit: state.page.limit };
      // Clear candidate list data to prevent old data from showing while loading new mode
      state.candidateList = {
        ...state.candidateList,
        data: {},
        isLoading: true,
      };
    },

    setReloadConfig: (state, { payload: { value } }) => {
      state.reloadConfig = value;
    },

    applyFilter: (state, { payload }) => {
      const { key, value } = payload;
      state.filters[key] = value;
      // Reset page and scroll_token when filter changes, but preserve the current limit
      state.page = { ...initialState.page, limit: state.page.limit };
      state.filters.isFilterApplied = true;
    },

    setFiltersData: (state, { payload }) => {
      state.filterWithValues = payload;
    },

    applyJobFilter: (state, { payload }) => {
      state.filters = payload?.beConfig;
      // Reset page and scroll_token when filters change, but preserve the current limit
      state.page = { ...initialState.page, limit: state.page.limit };
      state.filters.isFilterApplied = true;
    },

    applySearch: (state, { payload }) => {
      state.searchTerm = payload?.searchTerm;
      // Reset page and scroll_token when search changes, but preserve the current limit
      state.page = { ...initialState.page, limit: state.page.limit };
    },

    applyPage: (state, { payload }) => {
      const { page, scroll_token } = payload;
      state.page.number = page;
      state.page.offset = state?.page?.number * state?.page?.limit;
      // Update scroll_token if provided in payload, otherwise keep the existing one
      if (scroll_token !== undefined) {
        state.page.scroll_token = scroll_token;
      }
    },

    updateScrollToken: (state, { payload }) => {
      const { scroll_token } = payload;
      state.page.scroll_token = scroll_token;
    },

    applyLimit: (state, { payload }) => {
      const { limit } = payload;
      state.page.limit = limit;
      // Reset page to 0 and clear scroll_token when limit changes
      state.page.number = 0;
      state.page.offset = 0;
      state.page.scroll_token = null;
    },

    applyActivitiesPage: (state, { payload }) => {
      const { page } = payload;
      state.currentApplicationActivitiesPage.page = page;
    },

    applyJobsActivitiesPage: (state, { payload }) => {
      const { page } = payload;
      state.currentJobActivitiesPage.page = page;
    },

    clearFilter: (state, { payload }) => {
      const { key } = payload;
      state.filters[key] = initialState.filters[key];
      // Reset page and scroll_token when filter is cleared, but preserve the current limit
      state.page = { ...initialState.page, limit: state.page.limit };
      state.filters.isFilterApplied = Object.values(state.filters).some(
        (filter) => filter !== initialState.filters[key]
      );
      state.filters.isFilterApplied = false;
    },

    clearAllFilter: (state) => {
      state.filters = { ...initialState.filters };
      // Reset page and scroll_token when all filters are cleared, but preserve the current limit
      state.page = { ...initialState.page, limit: state.page.limit };
    },

    clearData: (state) => {
      state.sort = { ...initialState.sort };
      state.currentView = initialState.currentView;
      state.searchTerm = '';
    },

    increaseRejectCount: (state) => {
      state.rejectedCount.data.total_applications =
        +state?.rejectedCount?.data?.total_applications + 1;
    },

    // Optimistically shift the stage counters when a bulk move is fired,
    // so the metric ribbon updates in sync with the candidate list's
    // optimistic filtering. The eventual getStagesCount refetch
    // (triggered after the bulk activity polling completes) reconciles
    // with the server's authoritative state.
    //
    // Skips pseudo-stages ('all_stages', 'rejected') as a source because
    // candidates being moved out of "All Stages" view have heterogeneous
    // source stages we don't track here.
    adjustStageCountsForMove: (state, { payload }) => {
      const { fromStageId, toStageId, count } = payload || {};
      if (!fromStageId || !toStageId || !count) return;
      const fromKey = String(fromStageId);
      if (fromKey === 'all_stages' || fromKey === 'rejected') return;

      const stageCounts = state?.getStagesCount?.data?.stage_counts;
      if (!Array.isArray(stageCounts)) return;

      const fromEntry = stageCounts.find(
        (s) => String(s?.stage_id) === fromKey
      );
      if (fromEntry) {
        fromEntry.applications_count = Math.max(
          0,
          (Number(fromEntry.applications_count) || 0) - count
        );
      }

      const toKey = String(toStageId);
      const toEntry = stageCounts.find((s) => String(s?.stage_id) === toKey);
      if (toEntry) {
        toEntry.applications_count =
          (Number(toEntry.applications_count) || 0) + count;
      } else {
        stageCounts.push({
          stage_id: toStageId,
          applications_count: count,
        });
      }
    },

    setLoadingState: (state, { payload }) => {
      const { key, isLoading } = payload || {};
      if (state[key]) {
        state[key].isLoading = isLoading;
      }
    },

    setApiData: (state, { payload }) => {
      const { key, data, status, error, isLoading } = payload || {};
      if (state[key]) {
        state[key].data = data;
        state[key].isLoading = isLoading;
        state[key].status = status;
        if (error) {
          state[key].error = error;
        }
      }
      // Update scroll_token from candidateList API response for pagination
      // Only update if the value has changed to prevent unnecessary re-renders
      if (
        key === 'candidateList' &&
        data?.pagination?.scroll_token &&
        state.page.scroll_token !== data.pagination.scroll_token
      ) {
        state.page.scroll_token = data.pagination.scroll_token;
      }
      if (data?.ats_job?.ats_department_id) {
        // Both work-type shapes are stored; the consumer picks by flag. The
        // reducer cannot read the flag itself, and storing only the enum is why
        // the job-settings scorecard picker could not be scoped to a custom
        // work type — employment_type is null for one.
        const atsWorkTypeId =
          data?.ats_job?.ats_work_type?.id ?? data?.ats_job?.ats_work_type_id;
        state.scorecardParams = {
          department_ids: [data?.ats_job?.ats_department_id],
          office_ids: data?.ats_job?.ats_offices?.map((itr) => itr.id),
          employment_types: [data?.ats_job?.employment_type],
          ats_work_type_ids: atsWorkTypeId ? [atsWorkTypeId] : [],
        };
      }
    },
  },
});

export { atsJobDetailsActions };
export default reducer;
